// @file Utility functions for whiteboard shape checks and validation
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { TLPageId, TLShape } from 'tldraw'

/**
 * Checks if a shape is a valid attachment shape that can be duplicated/copied.
 * Excludes shapes that are still uploading (postId = 0).
 *
 * @param shape - The shape to check
 * @returns true if the shape is a valid attachment shape
 */
export function isValidAttachmentShape(shape: TLShape): shape is PadletAttachmentShape {
  return (
    shape.type === 'padlet-attachment-shape' &&
    (shape as PadletAttachmentShape).props.postId != null &&
    (shape as PadletAttachmentShape).props.postId !== 0 // Exclude attachments that are still uploading
  )
}

/**
 * Gets all valid attachment shapes for a given frame.
 * Excludes shapes that are still uploading.
 *
 * @param frameId - The frame ID to get attachment shapes from
 * @returns Array of valid attachment shapes in the frame
 */
export function getValidAttachmentShapesForFrame(frameId: TLPageId): PadletAttachmentShape[] {
  const editor = useTldrawEditor().editor
  if (editor == null) return []

  const allShapes = Array.from(editor.getPageShapeIds(frameId)).map((shapeId) => editor.getShape(shapeId))
  const attachmentShapes = allShapes.filter(
    (shape): shape is PadletAttachmentShape => shape != null && isValidAttachmentShape(shape),
  )

  return attachmentShapes
}
